// Kenyan mobile numbers.
//
// A phone number in Wodi is not decoration. It is the M-Pesa identifier a payment prompt
// is sent to, the SMS destination for a result or an appointment, and one of the exact-
// match keys for patient search (CLAUDE.md, API and database conventions). Two spellings
// of the same number are two patients, and a digit out is somebody else's phone.
//
// So there is exactly one stored form — `2547XXXXXXXX` or `2541XXXXXXXX`, twelve digits,
// no plus, no spaces — and everything else is either normalised into it or rejected with
// a code. Nothing is accepted "as typed".

import type { Result } from './result.ts';
import { err, ok } from './result.ts';

/**
 * A normalised Kenyan mobile number: country code and national number, digits only, as
 * stored and as sent to Daraja. Use `normalisePhone` to make one and `isNormalisedPhone`
 * to check a value that came from outside.
 */
export type Msisdn = string;

const KENYA_COUNTRY_CODE = '254';

/** Kenyan national numbers are nine digits after the trunk `0`. */
const NATIONAL_NUMBER_LENGTH = 9;

/**
 * Mobile numbers are the 07 and 01 series. Narrower checks (that 01 numbers are 010 or
 * 011, say) are deliberately not applied: the Communications Authority hands out new
 * blocks, and rejecting a number a patient actually owns is worse than accepting one that
 * is not yet in service — M-Pesa and the SMS gateway reject those anyway, visibly.
 */
const MOBILE_FIRST_DIGITS = new Set(['7', '1']);

export type PhoneErrorCode =
  | 'phone.empty'
  | 'phone.invalid_characters'
  | 'phone.unsupported_country'
  | 'phone.too_short'
  | 'phone.too_long'
  | 'phone.not_mobile';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const PHONE_ERRORS: Record<PhoneErrorCode, string> = {
  'phone.empty': 'No phone number was entered',
  'phone.invalid_characters': 'A phone number is digits only, with an optional leading +',
  'phone.unsupported_country': 'Only Kenyan numbers are supported',
  'phone.too_short': 'A Kenyan mobile number has nine digits after the 0',
  'phone.too_long': 'A Kenyan mobile number has nine digits after the 0',
  'phone.not_mobile': 'That is not a mobile number; M-Pesa and SMS need a 07 or 01 number',
};

/** Separators a receptionist types: `0712 345 678`, `0712-345-678`, `(020) 271 0000`. */
const SEPARATORS = /[\s()./-]/g;

/**
 * Turns anything a receptionist might type into the stored form, or says why it cannot be
 * one. Accepts `0712 345 678`, `0112345678`, `+254 712 345 678`, `254712345678`,
 * `00254712345678`, `712345678`, with spaces, dashes, dots or brackets anywhere.
 *
 * A `0` left in after a country code (`+254 0712 345 678`) is dropped: it is a common
 * slip, it has only one sensible reading, and refusing it teaches receptionists to retype
 * numbers rather than paste them.
 */
export function normalisePhone(input: string): Result<Msisdn, PhoneErrorCode> {
  const cleaned = input.replace(SEPARATORS, '');
  if (cleaned === '') return err('phone.empty');

  const hasPlus = cleaned.startsWith('+');
  const digits = hasPlus ? cleaned.slice(1) : cleaned;
  if (!/^\d+$/.test(digits)) return err('phone.invalid_characters');

  let national = digits;
  if (national.startsWith(`00${KENYA_COUNTRY_CODE}`)) {
    // The other international prefix: 00 254 ...
    national = national.slice(2 + KENYA_COUNTRY_CODE.length);
  } else if (national.startsWith(KENYA_COUNTRY_CODE)) {
    national = national.slice(KENYA_COUNTRY_CODE.length);
  } else if (hasPlus) {
    // A plus means a country code follows, and it is not Kenya's.
    return err('phone.unsupported_country');
  }

  // The trunk prefix, whether it was typed on its own or left in after a country code.
  if (national.startsWith('0')) national = national.slice(1);

  if (national.length < NATIONAL_NUMBER_LENGTH) return err('phone.too_short');
  if (national.length > NATIONAL_NUMBER_LENGTH) return err('phone.too_long');

  const firstDigit = national.slice(0, 1);
  if (!MOBILE_FIRST_DIGITS.has(firstDigit)) return err('phone.not_mobile');

  return ok(KENYA_COUNTRY_CODE + national);
}

/** True for a value already in the stored form. Use on anything read from outside. */
export function isNormalisedPhone(value: string): boolean {
  return /^254[71]\d{8}$/.test(value);
}

function nationalNumberOf(phone: Msisdn): string {
  if (!isNormalisedPhone(phone)) throw new RangeError('Not a normalised Kenyan mobile number');
  return phone.slice(KENYA_COUNTRY_CODE.length);
}

/**
 * `0712 345 678` — how the number is written on a receipt, a card and a queue list, and
 * how a patient reads it back. DESIGN.md does not fix a phone format; this is the local
 * convention, grouped so a person can check it a digit-group at a time.
 */
export function formatPhone(phone: Msisdn): string {
  const national = nationalNumberOf(phone);
  return `0${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`;
}

/** `+254 712 345 678` — for anything that leaves the country, and for M-Pesa support calls. */
export function formatPhoneInternational(phone: Msisdn): string {
  const national = nationalNumberOf(phone);
  return `+${KENYA_COUNTRY_CODE} ${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`;
}
